// src/lib/jumps.ts
/** 
 * Utilities for jump indicators - T-13 functionality
 */

export interface Jump {
    id: string;
    startValue: number;
    endValue: number;
    label?: string;
    autoLabel?: boolean;
    color: string;
    direction: 'positive' | 'negative';
    selected: boolean;
    showLabel: boolean;
    lane?: number;
  }
  
  /**
   * Calculate the span of a jump (end - start)
   */
  export function calculateJumpSpan(jump: Jump): number {
    return jump.endValue - jump.startValue;
  }
  
  /**
   * Format jump span for auto-labeling
   * Uses fractions if denominator ≤ 16, otherwise decimal
   */
  export function formatJumpSpan(span: number): string {
    if (span === 0) return '0';
    
    // Check if it's a simple integer
    if (Number.isInteger(span)) {
      return span.toString();
    }
    
    // Try to represent as a simple fraction
    const fraction = decimalToSimpleFraction(Math.abs(span), 16);
    if (fraction && fraction.d <= 16) {
      const sign = span < 0 ? '-' : '';
      if (fraction.d === 1) {
        return `${sign}${fraction.n}`;
      }
      return `${sign}${fraction.n}/${fraction.d}`;
    }
    
    // Fallback to decimal with reasonable precision
    return span.toFixed(span < 1 ? 3 : span < 10 ? 2 : 1);
  }
  
  /**
   * Convert decimal to simple fraction with max denominator
   */
  function decimalToSimpleFraction(decimal: number, maxDenominator: number = 16): { n: number; d: number } | null {
    if (Number.isInteger(decimal)) {
      return { n: decimal, d: 1 };
    }
    
    // Check common fractions first
    const commonFractions = [
      { decimal: 0.5, n: 1, d: 2 },
      { decimal: 0.25, n: 1, d: 4 },
      { decimal: 0.75, n: 3, d: 4 },
      { decimal: 0.333333, n: 1, d: 3 },
      { decimal: 0.666667, n: 2, d: 3 },
      { decimal: 0.125, n: 1, d: 8 },
      { decimal: 0.375, n: 3, d: 8 },
      { decimal: 0.625, n: 5, d: 8 },
      { decimal: 0.875, n: 7, d: 8 },
      { decimal: 0.2, n: 1, d: 5 },
      { decimal: 0.4, n: 2, d: 5 },
      { decimal: 0.6, n: 3, d: 5 },
      { decimal: 0.8, n: 4, d: 5 },
    ];
    
    const wholePart = Math.floor(decimal);
    const fractionalPart = decimal - wholePart;
    
    // Find closest common fraction
    for (const frac of commonFractions) {
      if (Math.abs(fractionalPart - frac.decimal) < 0.001) {
        return { 
          n: wholePart * frac.d + frac.n, 
          d: frac.d 
        };
      }
    }
    
    // Try brute force for small denominators
    for (let d = 2; d <= maxDenominator; d++) {
      const n = Math.round(decimal * d);
      if (Math.abs(decimal - n / d) < 0.001) {
        return { n, d };
      }
    }
    
    return null;
  }
  
  /**
   * Calculate vertical lanes for overlapping jumps
   * Implements collision avoidance by stacking jumps vertically
   */
  export function calculateJumpLanes(
    jumps: Jump[],
    valueToPixel: (value: number) => number,
    minSeparation: number = 40
  ): Jump[] {
    if (jumps.length === 0) return [];
    
    // Create jump intervals with pixel positions
    const jumpIntervals = jumps.map(jump => ({
      jump,
      startPx: Math.min(valueToPixel(jump.startValue), valueToPixel(jump.endValue)),
      endPx: Math.max(valueToPixel(jump.startValue), valueToPixel(jump.endValue)),
      width: Math.abs(valueToPixel(jump.endValue) - valueToPixel(jump.startValue))
    }));
    
    // Sort by start position
    jumpIntervals.sort((a, b) => a.startPx - b.startPx);
    
    // Assign lanes using greedy algorithm
    const lanes: Array<{ endPx: number; jumps: Jump[] }> = [];
    const result: Jump[] = [];
    
    for (const interval of jumpIntervals) {
      let assignedLane = -1;
      
      // Find the first lane where this jump can fit
      for (let laneIndex = 0; laneIndex < lanes.length; laneIndex++) {
        const lane = lanes[laneIndex];
        // Check if there's enough separation from the last jump in this lane
        if (interval.startPx >= lane.endPx + minSeparation) {
          assignedLane = laneIndex;
          break;
        }
      }
      
      // If no existing lane works, create a new one
      if (assignedLane === -1) {
        assignedLane = lanes.length;
        lanes.push({ endPx: interval.endPx, jumps: [] });
      }
      
      // Assign the jump to the lane
      const jumpWithLane: Jump = {
        ...interval.jump,
        lane: assignedLane
      };
      
      lanes[assignedLane].endPx = interval.endPx;
      lanes[assignedLane].jumps.push(jumpWithLane);
      result.push(jumpWithLane);
    }
    
    return result;
  }
  
  /**
   * Get auto-generated label for a jump
   */
  export function getJumpAutoLabel(jump: Jump): string {
    const span = calculateJumpSpan(jump);
    return formatJumpSpan(span);
  }
  
  /**
   * Get display label for a jump (manual or auto)
   */
  export function getJumpDisplayLabel(jump: Jump): string {
    if (jump.label) {
      return jump.label;
    }
    
    if (jump.autoLabel !== false) {
      return getJumpAutoLabel(jump);
    }
    
    return ''; // No label
  }
  
  /**
   * Check if two jumps overlap horizontally
   */
  export function jumpsOverlap(
    jump1: Jump,
    jump2: Jump,
    valueToPixel: (value: number) => number,
    buffer: number = 20
  ): boolean {
    const start1 = Math.min(valueToPixel(jump1.startValue), valueToPixel(jump1.endValue));
    const end1 = Math.max(valueToPixel(jump1.startValue), valueToPixel(jump1.endValue));
    const start2 = Math.min(valueToPixel(jump2.startValue), valueToPixel(jump2.endValue));
    const end2 = Math.max(valueToPixel(jump2.startValue), valueToPixel(jump2.endValue));
    
    return !(end1 + buffer < start2 || end2 + buffer < start1);
  }
  
  /**
   * Calculate curved arrow path for jump visualization
   */
  export function createJumpArrowPath(
    startX: number,
    endX: number,
    y: number,
    height: number = 30,
    direction: 'positive' | 'negative' = 'positive'
  ): string {
    const width = Math.abs(endX - startX);
    const isReversed = endX < startX;
    const leftX = Math.min(startX, endX);
    const rightX = Math.max(startX, endX);
    
    // Control point for the curve (creates an arc)
    const midX = (leftX + rightX) / 2;
    const controlY = y - height;
    
    // Arrow direction
    const arrowSize = 8;
    const arrowX = isReversed ? leftX : rightX;
    const arrowY = y;
    
    // Create curved path
    let path = `M ${startX} ${y}`;
    
    if (width > 20) {
      // Curved jump for longer distances
      path += ` Q ${midX} ${controlY} ${endX} ${y}`;
    } else {
      // Straight line for short distances
      path += ` L ${endX} ${y}`;
    }
    
    // Add arrowhead
    if (isReversed) {
      // Left-pointing arrow
      path += ` M ${arrowX} ${arrowY}`;
      path += ` L ${arrowX + arrowSize} ${arrowY - arrowSize/2}`;
      path += ` M ${arrowX} ${arrowY}`;
      path += ` L ${arrowX + arrowSize} ${arrowY + arrowSize/2}`;
    } else {
      // Right-pointing arrow
      path += ` M ${arrowX} ${arrowY}`;
      path += ` L ${arrowX - arrowSize} ${arrowY - arrowSize/2}`;
      path += ` M ${arrowX} ${arrowY}`;
      path += ` L ${arrowX - arrowSize} ${arrowY + arrowSize/2}`;
    }
    
    return path;
  }
  
  /**
   * Calculate lane offset for vertical stacking
   */
  export function getLaneOffset(laneIndex: number, laneHeight: number = 45): number {
    return laneIndex * laneHeight;
  }
  
  /**
   * Get jump color based on direction
   */
  export function getJumpColor(direction: 'positive' | 'negative'): string {
    return direction === 'positive' ? '#22c55e' : '#ef4444';
  }
  
  /**
   * Update jump direction based on start/end values
   */
  export function updateJumpDirection(jump: Jump): Jump {
    const newDirection = jump.endValue >= jump.startValue ? 'positive' : 'negative';
    return {
      ...jump,
      direction: newDirection,
      color: getJumpColor(newDirection)
    };
  }
  
  /**
   * Validate jump creation bounds
   */
  export function isValidJump(startValue: number, endValue: number): boolean {
    // Allow zero-length jumps for point markers
    return Number.isFinite(startValue) && Number.isFinite(endValue);
  }
  
  /**
   * Create a new jump with default properties
   */
  export function createJumpDefaults(startValue: number, endValue: number): Omit<Jump, 'id'> {
    const direction = endValue >= startValue ? 'positive' : 'negative';
    return {
      startValue,
      endValue,
      autoLabel: true,
      color: getJumpColor(direction),
      direction,
      selected: false,
      showLabel: true
    };
  }